"use client"

import clsx from "clsx"
import React from "react"

import FormFieldBase from "./FormFieldBase"
import { useFormErrorContext } from "./StepWrapper"

interface DateOfBirthField {
  value: { day: string; month: string; year: string }
  setValue: (value: { day: string; month: string; year: string }) => void
  isValid: boolean
  touched: boolean
  blurred: boolean
  setTouched: (value: boolean) => void
  setBlurred: (value: boolean) => void
  errorMessage?: string | null
}

interface WebNativeDateInputProps {
  label?: string
  className?: string
  dateOfBirth: DateOfBirthField
  id?: string
  name?: string
}

const DateInput: React.FC<WebNativeDateInputProps> = ({ label, className, dateOfBirth, id, name }) => {
  const clearFormError = useFormErrorContext()
  const inputRef = React.useRef<HTMLInputElement>(null)

  const { isValid, touched, blurred, errorMessage } = dateOfBirth
  const showError = !isValid && touched && blurred && errorMessage?.trim()

  const hasValue = Boolean(dateOfBirth.value.year || dateOfBirth.value.month || dateOfBirth.value.day)

  const maxDate = React.useMemo(() => {
    return new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split("T")[0]
  }, [])

  const formatDateValue = () => {
    if (!dateOfBirth.value.year && !dateOfBirth.value.month && !dateOfBirth.value.day) {
      return ""
    }

    const year = dateOfBirth.value.year || ""
    const month = dateOfBirth.value.month ? dateOfBirth.value.month.padStart(2, "0") : ""
    const day = dateOfBirth.value.day ? dateOfBirth.value.day.padStart(2, "0") : ""

    if (!year && !month && !day) return ""
    return `${year || "2000"}-${month || "01"}-${day || "01"}`
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (clearFormError) {
      clearFormError()
    }

    const dateValue = e.target.value

    if (dateValue) {
      const [year, month, day] = dateValue.split("-")
      dateOfBirth.setValue({
        day: day || "",
        month: month || "",
        year: year || "",
      })
    } else {
      dateOfBirth.setValue({
        day: "",
        month: "",
        year: "",
      })
    }

    dateOfBirth.setTouched(true)
  }

  const handleBlur = () => {
    dateOfBirth.setTouched(true)
    dateOfBirth.setBlurred(true)
  }

  const handleFocus = () => {
    dateOfBirth.setTouched(true)
    dateOfBirth.setBlurred(false)
  }

  return (
    <FormFieldBase errorMessage={errorMessage} isValid={isValid} touched={touched} blurred={blurred} hasValue={hasValue}>
      <div className="relative flex flex-col">
        {label && (
          <div className={clsx("absolute -top-1 left-0 text-xs font-bold", showError ? "text-[#C20000]" : "text-gray-700")}>{label}</div>
        )}
        <div className="relative">
          <input
            ref={inputRef}
            className={clsx(
              "date-input-custom w-full appearance-none bg-transparent pb-0 pl-0 pr-10 pt-3 text-left transition-all duration-200 focus:outline-none",
              {
                "text-gray-400": !hasValue,
                "text-black": hasValue,
              },
              className
            )}
            onChange={handleChange}
            value={formatDateValue()}
            type="date"
            onBlur={handleBlur}
            onFocus={handleFocus}
            id={id}
            name={name}
            min="1900-01-01"
            max={maxDate}
            lang="es-MX"
          />
        </div>
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3">
          {showError && (
            <div className="flex h-6 w-6 items-center justify-center rounded-full border-[1.5px] border-[#C20000] text-[#C20000]">
              <span className="font-bold">!</span>
            </div>
          )}
          {isValid && !showError && hasValue && (
            <div className="text-blue-500">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          )}
        </div>
      </div>
    </FormFieldBase>
  )
}

export default DateInput
